import { LegalPage, Section, SubSection } from "./LegalPage";

export function CreatorAgreement() {
  return (
    <LegalPage title="Creator & Seller Agreement" lastUpdated="March 20, 2026">
      <p>This Creator Agreement ("Agreement") governs your participation as a seller on the SimSense Marketplace. By publishing content on the Marketplace, you agree to these terms.</p>

      <Section title="1. Eligibility">
        <SubSection title="Community Sharers">
          <ul>
            <li>Any authenticated SimSense user may share free setups</li>
            <li>Free setups must be linked to a telemetry session as proof of use</li>
            <li>Community sharers are subject to all terms below except payment/commission sections</li>
          </ul>
        </SubSection>
        <SubSection title="Verified Creators">
          <ul>
            <li>Individual sellers must apply and be approved by SimSense</li>
            <li>Approval is based on: active SimSense account, history of quality uploads, and agreement to these terms</li>
            <li>Verified Creator status may be revoked for violations</li>
          </ul>
        </SubSection>
        <SubSection title="Setup Shops">
          <ul>
            <li>Shop accounts require a separate application and approval process</li>
            <li>Shops may have multiple contributors with per-shop permissions</li>
            <li>Shop owners are responsible for all content published under their shop</li>
          </ul>
        </SubSection>
      </Section>

      <Section title="2. Content Ownership & License">
        <SubSection title="Your Rights">
          <ul>
            <li>You retain full ownership and intellectual property rights to your setup files and content.</li>
            <li>SimSense does not claim ownership of your work.</li>
          </ul>
        </SubSection>
        <SubSection title="License to SimSense">
          <p>By publishing on the Marketplace, you grant SimSense a non-exclusive, worldwide license to:</p>
          <ul>
            <li>Host, store, and distribute your content through the platform</li>
            <li>Display previews, thumbnails, and telemetry proof data to potential buyers</li>
            <li>Use your shop name, logo, and listing details for platform marketing</li>
          </ul>
          <p>This license terminates when you remove your content from the Marketplace.</p>
        </SubSection>
        <SubSection title="License to Buyers">
          <p>When a buyer purchases your setup, they receive a personal-use license to:</p>
          <ul>
            <li>Download and install the setup in their iRacing installation</li>
            <li>Use the setup for racing (online and offline)</li>
          </ul>
          <p>Buyers may NOT redistribute, resell, or share purchased setups.</p>
        </SubSection>
      </Section>

      <Section title="3. Commission & Payments">
        <SubSection title="Platform Commission">
          <p>SimSense retains <strong>20%</strong> of each paid sale as platform commission.</p>
        </SubSection>
        <SubSection title="Pricing">
          <ul>
            <li>Minimum price for paid listings: <strong>$0.99</strong></li>
            <li>Pricing is set by the creator/shop</li>
            <li>SimSense may offer platform-wide promotions (e.g., Pro subscriber discounts) — in these cases, SimSense absorbs the discount and creators still receive their full cut.</li>
          </ul>
        </SubSection>
        <SubSection title="Payouts">
          <ul>
            <li>Payouts are processed monthly for balances exceeding $10</li>
            <li>Payout method: Stripe Connect (bank transfer or Stripe balance)</li>
            <li>Creators are responsible for their own tax obligations</li>
          </ul>
        </SubSection>
      </Section>

      <Section title="4. Content Standards">
        <SubSection title="Acceptable Content">
          <ul>
            <li>iRacing setup files (.sto format)</li>
            <li>Setup bundles/season packs</li>
            <li>Accurate descriptions, car/track metadata, and season compatibility</li>
          </ul>
        </SubSection>
        <SubSection title="Prohibited Content">
          <ul>
            <li>Malware, viruses, or harmful code embedded in files</li>
            <li>Setups you do not own or have rights to distribute</li>
            <li>Content that impersonates another creator, shop, or brand</li>
            <li>Deceptive listings (wrong car/track, fake performance claims)</li>
            <li>Bulk-generated or untested setups</li>
            <li>Content that violates iRacing's Terms of Service</li>
          </ul>
        </SubSection>
      </Section>

      <Section title="5. Brand Protection">
        <ul>
          <li>Registered brand names and keywords are protected by SimSense's Commercial Protection system</li>
          <li>Only authorized publishers may use protected brand keywords in setup names</li>
          <li>Unauthorized use of protected names will be flagged and may result in listing removal</li>
          <li>Creators may request brand protection for their shop name by contacting SimSense</li>
        </ul>
      </Section>

      <Section title="6. Reviews & Reputation">
        <ul>
          <li>Buyers may leave reviews on purchased setups</li>
          <li>Creators may NOT incentivize, buy, or manipulate reviews</li>
          <li>SimSense reserves the right to remove fraudulent reviews</li>
          <li>Creators may respond to reviews through the platform</li>
        </ul>
      </Section>

      <Section title="7. Disputes & Takedowns">
        <SubSection title="IP Disputes">
          <ul>
            <li>If you believe your content has been copied, submit a takedown request to support@outlierspeedco.com</li>
            <li>SimSense will review and act on valid claims within 5 business days</li>
            <li>Counter-notifications may be filed by the accused party</li>
          </ul>
        </SubSection>
        <SubSection title="Refunds">
          <ul>
            <li>SimSense handles buyer refund requests</li>
            <li>Refunds may be issued for: duplicate purchases, technical failures, or clearly misleading listings</li>
            <li>Refunded commission is deducted from creator payouts</li>
          </ul>
        </SubSection>
      </Section>

      <Section title="8. Termination">
        <ul>
          <li>You may remove your listings and close your seller account at any time</li>
          <li>SimSense may suspend or terminate seller accounts for repeated content violations, fraudulent activity, or abuse of the review system</li>
          <li>Existing buyer licenses survive creator account termination</li>
        </ul>
      </Section>

      <Section title="9. Changes to This Agreement">
        <p>SimSense may update this Agreement with 30 days notice. Continued publishing after changes constitutes acceptance.</p>
      </Section>

      <Section title="10. Contact">
        <p>For creator/seller inquiries: <strong>support@outlierspeedco.com</strong></p>
      </Section>
    </LegalPage>
  );
}
